import type { Logout } from 'flowhawk-core';
import type { Reducer } from '@reduxjs/toolkit';

import type { ResetData } from './ApplicationActions';
import type { FollowerEditState } from './FollowerEditActions';
import type { UpdateAlertStatusSuccess } from './CaseAlertsActions';
import type {
  Donor,
  DonorSortBy,
  SortDirection,
  DonorMap,
  FileMap,
  MessageId,
  AttachmentData,
  FollowerGroupMap,
  UserProfile,
  StaticTask,
  WorkflowTaskMap,
  DonorTask,
  FormValueMap,
  TaskDataMap,
  Tag,
  DonorDataEntry,
  DonorImportField,
  CaseNote,
  CaseTimelineEntry,
  FormDefData,
  LoadCasesParams,
  CaseStage,
  SelectedSections,
  AppFile,
  CaseServiceAlert,
  CaseView,
  CreateCaseViewParams,
  UpdateCaseViewParams,
  DonorSource,
} from '../Utils/types';
import { OrganizationAlertStatus } from '../Utils/types';
import type { ApiCaseServiceAlert } from '../Utils/apiTypes';
import { replaceWith } from '../Utils/array';

export const NOTIFIABLE_GROUP_ID_1 = 1; // standard donor tasks
export const NOTIFIABLE_GROUP_ID_2 = 2; // FlowHawk release tasks
export const NOTIFIABLE_GROUP_ID_3 = 3; // Experience Anatomy tasks

export type DonorErrors = {
  nonFieldErrors?: string;
  opoDonorId?: string;
  unosId?: string;
};

export type DonorState = {
  donors: DonorMap;
  openDonorId?: number;
  openFollowerGroupId?: number;

  workflowTasks: WorkflowTaskMap;
  donorTask: DonorTask;
  editNoteContent: string;

  opoDonorId: string;
  workflowKey: string;
  hospital: string;
  sex?: string;
  dateOfBirth?: string;
  unosId: string;
  organs: Array<string>;
  newRace: string;
  highRisk: boolean;
  tags: Array<Tag>;
  orgMembers: Array<UserProfile>;

  donorUpdated: boolean;

  createDonorSuccess: boolean;
  errors?: DonorErrors;

  files: Array<FileMap>;
  fileIds: Array<MessageId>;
  filter: string;
  xmatFiles: FileMap;

  availableTags: Array<Tag>;
  caseTagFilter: string[];
  pdfImportFields: Array<DonorImportField>;
  casePreferences: Array<FollowerEditState>; // NOTE: we may add more than just follower info to this in the future

  sort: {
    column: DonorSortBy;
    direction: SortDirection;
  };

  workflowTypes: string[];
  totalFilteredCases: number;
  caseDisplayOrder: number[];
  shouldRefreshCaseList: boolean;

  sessionCaseStatusFilter: string;
  sessionCaseStageFilter: CaseStage[];

  // Per-case expanded task accordion and blank fields toggle
  caseExpandedTasks: Record<number, number[]>;
  caseShowBlankFields: Record<number, boolean>;

  // For multi-select mode in donor list
  selectionModeEnabled: boolean;
  selectedDonorIds: number[];
};

export const donorSortDefault = {
  column: 'CREATE_DATE' as DonorSortBy,
  direction: 'desc' as SortDirection,
};

const initialState: DonorState = {
  donors: {} as DonorMap,
  openDonorId: undefined,
  openFollowerGroupId: undefined,

  workflowTasks: {},
  donorTask: {} as DonorTask,
  editNoteContent: '',

  opoDonorId: '',
  workflowKey: '',
  hospital: '',
  sex: undefined,
  dateOfBirth: undefined,
  unosId: '',
  organs: [],
  newRace: '',
  highRisk: false,
  tags: [],
  orgMembers: [],

  donorUpdated: false,

  createDonorSuccess: false,
  errors: undefined,

  files: [],
  fileIds: [],
  filter: '',
  xmatFiles: {} as FileMap,

  availableTags: [],
  caseTagFilter: [],
  pdfImportFields: [],

  workflowTypes: [],
  casePreferences: [],

  sessionCaseStatusFilter: 'Open',
  sessionCaseStageFilter: [],

  sort: donorSortDefault,
  totalFilteredCases: 0,
  caseDisplayOrder: [],
  shouldRefreshCaseList: false,

  caseExpandedTasks: {},
  caseShowBlankFields: {},

  selectionModeEnabled: false,
  selectedDonorIds: [],
};

export type LoadCases = {
  type: 'Donor/LOAD_CASES';
  params: LoadCasesParams;
};
export type ReceiveDonors = { type: 'Donor/RECEIVE_DONORS'; donors: DonorMap };
export type GetDonor = { type: 'Donor/GET_DONOR'; donorId: number };
export type ReceiveSingleDonor = { type: 'Donor/RECEIVE_SINGLE_DONOR'; donorId: number; donor: Donor };
export type OpenDonor = { type: 'Donor/OPEN_DONOR'; openDonorId: number };
export type CloseDonor = { type: 'Donor/CLOSE_DONOR'; donorId: number };
export type UpdateDonorStatus = {
  type: 'Donor/UPDATE_DONOR_STATUS';
  donorId: number;
  closed: boolean;
  lastModified: string;
};

export type SetOPODonorId = { type: 'Donor/SET_OPO_ID'; opoDonorId: string };
export type SetUnosId = { type: 'Donor/SET_UNOS_ID'; unosId: string };
export type SetWorkflowKey = { type: 'Donor/SET_WORKFLOW_KEY'; workflowKey: string };
export type SetHospital = { type: 'Donor/SET_HOSPITAL'; hospital: string };
export type SetSex = { type: 'Donor/SET_SEX'; sex?: string };
export type SetDateOfBirth = { type: 'Donor/SET_DOB'; dateOfBirth: string };
export type SetSelectedOrgans = { type: 'Donor/SET_SELECTED_ORGANS'; organs: Array<string> };
export type SetNewRace = { type: 'Donor/SET_RACE'; newRace: string };
export type SetHighRisk = { type: 'Donor/SET_HIGH_RISK'; highRisk: boolean };
export type SetTags = { type: 'Donor/SET_TAGS'; tags: Array<Tag> };
export type SetSort = { type: 'Donor/SET_SORT'; column: DonorSortBy; direction: SortDirection };

export type GetOrganizationMembers = { type: 'Donor/GET_ORG_MEMBERS' };
export type ReceiveOrganizationMembers = { type: 'Donor/RECEIVE_ORG_MEMBERS'; orgMembers: Array<UserProfile> };

export type ReceiveAvailableTags = { type: 'Donor/RECEIVE_AVAILABLE_TAGS'; availableTags: Array<Tag> };
export type ReceivePdfFields = { type: 'Donor/RECEIVE_PDF_FIELDS'; pdfFields: Array<DonorImportField> };
export type LoadPdfFields = { type: 'Donor/LOAD_PDF_FIELDS' };

export type ImportDonorData = {
  type: 'Donor/IMPORT_DATA';
  caseId: number;
  importedDonorData: DonorDataEntry[];
  donorSource: DonorSource;
  existingDonorId?: number;
};
export type ImportDonorDataSuccess = { type: 'Donor/IMPORT_DATA_SUCCESS'; caseId: number };
export type ImportDonorDataFailed = { type: 'Donor/IMPORT_DATA_FAILED' };

export type CreateDonor = {
  type: 'Donor/CREATE';
  opoDonorId: string;
  workflowKey: string;
  highRisk: boolean;
  tags: Array<Tag>;
  hospital: string;
  sex?: string;
  dateOfBirth: string;
  unosId: string;
  organs: Array<string>;
  importedDonorData?: Array<DonorDataEntry>;
  donorNetPdf?: File;
  existingDonorId?: number;
  selectedProviderServices?: Array<string>;
  stage?: CaseStage | null;
  isUnosImport?: boolean;
};
export type CreateDonorFailed = { type: 'Donor/CREATE_FAILED'; errors?: DonorErrors };
export type CreateDonorSuccess = { type: 'Donor/CREATE_SUCCESS'; openDonorId: number };
export type ResetCreateDonorSuccess = { type: 'Donor/RESET_CREATE_SUCCESS' };

export type UpdateDonor = {
  type: 'Donor/UPDATE';
  donorId: number;
  lastModified: string;
  opoDonorId: string;
  highRisk: boolean;
  tags: Array<Tag>;
  unosId: string | null;
  currentLocation: string;
  dob: string;
  typeOfDeath: string | null;
  causeOfDeath: string | null;
  race: string | null;
  sex: string | null;
  weight: string;
  height: string;
  organs: Array<string>;
  stage: CaseStage | null;
  rank: number | null;
  sequenceNumber: number | null;
};
export type UpdateDonorSuccess = { type: 'Donor/UPDATE_SUCCESS'; donorId: number; donor: Donor };

export type PatchUpdateCase = {
  type: 'Donor/PATCH_UPDATE_CASE';
  caseId: number;
  lastModified: string;
  fields: {
    opoDonorId?: string | null;
    stage?: CaseStage | null;
    rank?: number | null;
    sequenceNumber?: number | null;
    highRisk?: boolean;
    tags?: Tag[] | null;
    xmatCaseId?: string | null;
    perfusionDeviceSn?: string | null;
  };
};

export type DonorChanged = { type: 'Donor/CHANGED'; donorUpdated: boolean };

export type FilterDonors = { type: 'Donor/FILTER_DONORS'; filter: string };

export type LoadDonorFiles = { type: 'Donor/LOAD_FILES'; donorId: number };
export type ReceiveDonorFiles = {
  type: 'Donor/RECEIVE_FILES';
  donorId: number;
  fileMap: FileMap;
  fileIds: Array<MessageId>;
};
export type SetDonorSelectedFile = {
  type: 'Donor/SET_SELECTED_FILE';
  id: number;
  selectedFile: AppFile;
};
export type UploadDonorFile = {
  type: 'Donor/UPLOAD_DONOR_FILE';
  donorId: number;
  attachments: Array<AttachmentData>;
  isCaseAttachment?: boolean;
};
export type UploadDonorFileSuccess = { type: 'Donor/UPLOAD_DONOR_FILE_SUCCESS' };
export type UploadDonorFileFailed = { type: 'Donor/UPLOAD_DONOR_FILE_FAILED'; message: string };

export type DeleteDonorFile = { type: 'Donor/DELETE_DONOR_FILE'; mediaFileId: number; donorId: number };
export type DeleteDonorFileSuccess = { type: 'Donor/DELETE_DONOR_FILE_SUCCESS' };
export type DeleteDonorFileFailed = { type: 'Donor/DELETE_DONOR_FILE_FAILED'; message: string };

export type ReceiveTasks = {
  type: 'Donor/RECEIVE_TASKS';
  workflow: string;
  tasks: Array<StaticTask>;
  sectionOrder: Array<string>;
};
export type GetDonorTask = { type: 'Donor/GET_DONOR_TASK'; donorId: number; taskId: number };
export type ReceiveDonorTask = { type: 'Donor/RECEIVE_DONOR_TASK'; donorTask: DonorTask };
export type GetDonorTaskData = { type: 'Donor/GET_DONOR_TASK_DATA'; donorId: number };
export type ReceiveDonorTaskData = { type: 'Donor/RECEIVE_DONOR_TASK_DATA'; donorId: number; taskData: TaskDataMap };

export type OpenFollowerGroup = { type: 'Donor/OPEN_FOLLOWER_GROUP'; followerGroupId?: number };
export type CloseFollowerGroup = { type: 'Donor/CLOSE_FOLLOWER_GROUP'; followerGroupId: number };
export type GetDonorFollowerGroups = { type: 'Donor/GET_DONOR_FOLLOWER_GROUPS'; donorId: number };
export type GetFollowerGroup = {
  type: 'Donor/GET_FOLLOWER_GROUP';
  donorId: number;
  followerGroupId: number;
  // Whether this fetch should also populate the followerEdit scratch state (initFollowerEditData).
  // Only true when the user is deliberately opening this group to view/edit it - background refreshes
  // (socket-driven member-list syncs, the join/leave toggle's own internal refetch, etc.) must pass
  // false so they don't clobber followerEdit out from under an unrelated in-progress edit or reset.
  initEditState: boolean;
};
export type ReceiveDonorFollowerGroups = {
  type: 'Donor/RECEIVE_DONOR_FOLLOWER_GROUPS';
  donorId: number;
  followerGroupMap: FollowerGroupMap;
};
export type UpdateFollowerGroupTasks = {
  type: 'Donor/UPDATE_FOLLOWER_GROUP_TASKS';
  donorId: number;
  followerGroupId: number;
  taskIds: number[];
  muteNotification?: boolean;
};
export type UpdateFollowerGroupMembers = {
  type: 'Donor/UPDATE_FOLLOWER_GROUP_MEMBERS';
  donorId: number;
  followerGroupId: number;
  addUserIds: Array<number>;
  removeUserIds: Array<number>;
  addExternalUserIds: Array<number>;
  removeExternalUserIds: Array<number>;
  muteNotification?: boolean;
};
export type FollowerGroupMemberChanges = {
  addUserIds?: Array<number>;
  removeUserIds?: Array<number>;
  addExternalUserIds?: Array<number>;
  removeExternalUserIds?: Array<number>;
};
export type UpdateFollowerGroupPermissions = {
  type: 'Donor/UPDATE_FOLLOWER_GROUP_PERMISSIONS';
  donorId: number;
  followerGroupId: number;
  canUpdate: boolean;
};
export type DeleteFollowerGroup = { type: 'Donor/DELETE_FOLLOWER_GROUP'; donorId: number; followerGroupId: number };

export type UpdateTaskStatus = {
  type: 'Donor/UPDATE_TASK_STATUS';
  donorId: number;
  taskId: number;
  lastModified: string;
  fieldValueMap?: FormValueMap;
  completed?: boolean;
  notApplicable?: boolean;
  fromList: boolean;
  formDef: FormDefData;
};
export type UpdateTaskDueDate = {
  type: 'Donor/UPDATE_TASK_DUE_DATE';
  donorId: number;
  taskId: number;
  previousTaskId: number | null;
  interval: number | null;
  lastModified: string;
  dueDateComment: string | null;
};
export type UpdateTaskNote = {
  type: 'Donor/UPDATE_TASK_NOTE';
  donorId: number;
  taskId: number;
  noteContent: string;
  lastModified: string;
};
export type SaveTaskNote = {
  type: 'Donor/SAVE_TASK_NOTE';
  noteContent: string;
};
export type UpdateDonorForm = {
  type: 'Form/UPDATE_DONOR_FORM';
  donorId: number;
  taskId: number;
  formValueMap: FormValueMap;
  lastModified: string;
  notify: boolean;
  notifyFullTask: boolean;
  notifyAllTasks: boolean;
};

export type PinCase = { type: 'Donor/PIN_CASE'; caseId: number };
export type PinnedCase = { type: 'Donor/PINNED_CASE'; caseId: number; pinNumber: number };
export type UnpinCase = { type: 'Donor/UNPIN_CASE'; caseId: number };
export type UnpinnedCase = { type: 'Donor/UNPINNED_CASE'; caseId: number };

export type ResetDonorEditState = { type: 'Donor/RESET' };

export type DonorTagFilters = {
  type: 'Donor/RECEIVE_TAG_FILTER';
  tags: string[];
};

export type WorkflowTypeFilters = {
  type: 'Donor/RECEIVE_WORKFLOW_FILTER';
  workflowTypes: string[];
};

export type SessionCaseStatusFilter = {
  type: 'Donor/RECEIVE_SESSION_CASE_FILTER';
  sessionCaseStatusFilter: string;
};

export type SessionCaseStageFilter = {
  type: 'Donor/RECEIVE_SESSION_CASE_STAGE_FILTER';
  sessionCaseStageFilter: CaseStage[];
};

export type ReceiveCaseNotes = { type: 'Donor/RECEIVE_CASE_NOTES'; caseId: number; notes: CaseNote[] };
export type GetCaseTimeline = { type: 'Donor/GET_CASE_TIMELINE'; caseId: number };
export type ReceiveCaseTimeline = {
  type: 'Donor/RECEIVE_CASE_TIMELINE';
  caseId: number;
  timeline: CaseTimelineEntry[];
};
export type SetCasePreferences = { type: 'Donor/SET_CASE_PREFERENCES'; casePreferences: FollowerEditState[] };
export type ResetCasePreferences = { type: 'Donor/RESET_CASE_PREFERENCES' };
// NOTE: Eventually we shouldn't need to the donorId for loading case preferences if they are just tied to a workflow
// and now the last case
export type LoadCasePreferences = { type: 'Donor/LOAD_CASE_PREFERENCES'; workflow: string; caseId: number };
export type ReceiveTotalFilteredCases = { type: 'Donor/RECEIVE_TOTAL_FILTERED_CASES'; totalFilteredCases: number };
export type SetCaseDisplayOrder = { type: 'Donor/SET_CASE_DISPLAY_ORDER'; caseDisplayOrder: number[] };
export type IncrementCaseUnreadCount = { type: 'Donor/INCREMENT_CASE_UNREAD_COUNT'; caseId: number };

export type GenerateModularCasePDF = {
  type: 'Donor/GENERATE_MODULAR_CASE_PDF';
  caseId: number;
  sections: SelectedSections;
};

export type SetEditCaseTaskStatus = {
  type: 'Donor/SET_EDIT_CASE_TASK_STATUS';
  caseId: number;
  taskId: number;
  editBy?: number;
};
export type ReceiveEditCaseTaskStatus = {
  type: 'Donor/RECEIVE_EDIT_CASE_TASK_STATUS';
  caseId: number;
  taskId: number;
  editBy?: number;
  editTime?: string;
};

export type EnableSelectionMode = { type: 'Donor/ENABLE_SELECTION_MODE' };

export type DisableSelectionMode = { type: 'Donor/DISABLE_SELECTION_MODE' };

export type ResetDonorSelection = { type: 'Donor/RESET_DONOR_SELECTION'; donorIds?: number[] };

export type ToggleDonorSelection = { type: 'Donor/TOGGLE_DONOR_SELECTION'; donorId: number };

export type ToggleCaseTaskExpanded = { type: 'Donor/TOGGLE_CASE_TASK_EXPANDED'; donorId: number; taskId: number };

export type SetCaseShowBlankFields = {
  type: 'Donor/SET_CASE_SHOW_BLANK_FIELDS';
  donorId: number;
  showBlankFields: boolean;
};

export type ReceiveOrganizationAlert = {
  type: 'Donor/RECEIVE_ORGANIZATION_ALERT';
  caseId: number;
  alertId: number;
  alert: Partial<ApiCaseServiceAlert>;
};
export type SetShouldRefreshCaseList = { type: 'Donor/SET_SHOULD_REFRESH_CASE_LIST'; shouldRefreshCaseList: boolean };

export type LoadProviderServices = { type: 'Donor/LOAD_PROVIDER_SERVICES'; providerId: string };

// CaseView actions
export type CreateCaseView = {
  type: 'Donor/CREATE_CASE_VIEW';
  params: CreateCaseViewParams;
};
export type CreateCaseViewSuccess = {
  type: 'Donor/CREATE_CASE_VIEW_SUCCESS';
  caseView: CaseView;
};
export type CreateCaseViewFailed = {
  type: 'Donor/CREATE_CASE_VIEW_FAILED';
  error: string;
};

export type UpdateCaseView = {
  type: 'Donor/UPDATE_CASE_VIEW';
  params: UpdateCaseViewParams;
};
export type UpdateCaseViewSuccess = {
  type: 'Donor/UPDATE_CASE_VIEW_SUCCESS';
  caseView: CaseView;
};
export type UpdateCaseViewFailed = {
  type: 'Donor/UPDATE_CASE_VIEW_FAILED';
  error: string;
};

export type DeleteCaseView = {
  type: 'Donor/DELETE_CASE_VIEW';
  casesViewId: number;
};
export type DeleteCaseViewSuccess = {
  type: 'Donor/DELETE_CASE_VIEW_SUCCESS';
  casesViewId: number;
};
export type DeleteCaseViewFailed = {
  type: 'Donor/DELETE_CASE_VIEW_FAILED';
  error: string;
};
export type RemoveCaseAdminFromCase = {
  type: 'Donor/REMOVE_CASE_ADMIN_FROM_CASE';
  caseId: number;
  userId: number;
};

type Action =
  | LoadCases
  | ReceiveDonors
  | GetDonor
  | ReceiveSingleDonor
  | OpenDonor
  | CloseDonor
  | UpdateDonorStatus
  | SetOPODonorId
  | SetUnosId
  | SetWorkflowKey
  | SetHospital
  | SetSex
  | SetDateOfBirth
  | SetSelectedOrgans
  | SetNewRace
  | SetHighRisk
  | SetTags
  | SetSort
  | GetOrganizationMembers
  | ReceiveOrganizationMembers
  | ReceiveAvailableTags
  | ReceivePdfFields
  | LoadPdfFields
  | ImportDonorData
  | ImportDonorDataSuccess
  | ImportDonorDataFailed
  | CreateDonor
  | CreateDonorFailed
  | CreateDonorSuccess
  | ResetCreateDonorSuccess
  | UpdateDonor
  | UpdateDonorSuccess
  | DonorChanged
  | FilterDonors
  | LoadDonorFiles
  | ReceiveDonorFiles
  | SetDonorSelectedFile
  | UploadDonorFile
  | UploadDonorFileSuccess
  | UploadDonorFileFailed
  | DeleteDonorFile
  | UpdateTaskDueDate
  | UpdateTaskNote
  | SaveTaskNote
  | UpdateDonorForm
  | ReceiveTasks
  | GetDonorTask
  | ReceiveDonorTask
  | GetDonorTaskData
  | ReceiveDonorTaskData
  | ReceiveDonorFollowerGroups
  | OpenFollowerGroup
  | CloseFollowerGroup
  | UpdateTaskStatus
  | ResetDonorEditState
  | Logout
  | ResetData
  | DonorTagFilters
  | WorkflowTypeFilters
  | SessionCaseStatusFilter
  | SessionCaseStageFilter
  | ReceiveCaseNotes
  | GetCaseTimeline
  | ReceiveCaseTimeline
  | SetCasePreferences
  | ResetCasePreferences
  | LoadCasePreferences
  | ReceiveTotalFilteredCases
  | SetCaseDisplayOrder
  | IncrementCaseUnreadCount
  | GenerateModularCasePDF
  | SetEditCaseTaskStatus
  | ReceiveEditCaseTaskStatus
  | ReceiveOrganizationAlert
  | SetShouldRefreshCaseList
  | LoadProviderServices
  | UnpinCase
  | PinCase
  | PinnedCase
  | UnpinnedCase
  | UpdateAlertStatusSuccess
  | CreateCaseView
  | CreateCaseViewSuccess
  | CreateCaseViewFailed
  | DeleteCaseView
  | DeleteCaseViewSuccess
  | DeleteCaseViewFailed
  | EnableSelectionMode
  | DisableSelectionMode
  | ResetDonorSelection
  | ToggleDonorSelection
  | ToggleCaseTaskExpanded
  | SetCaseShowBlankFields
  | RemoveCaseAdminFromCase;

export const loadCases = (params: LoadCasesParams): LoadCases => ({
  type: 'Donor/LOAD_CASES',
  params,
});

export const receiveDonors = (donors: DonorMap): ReceiveDonors => ({
  type: 'Donor/RECEIVE_DONORS',
  donors,
});

export const getDonor = (donorId: number): GetDonor => ({
  type: 'Donor/GET_DONOR',
  donorId,
});

export const receiveSingleDonor = (donorId: number, donor: Donor): ReceiveSingleDonor => ({
  type: 'Donor/RECEIVE_SINGLE_DONOR',
  donorId,
  donor,
});

export const openDonor = (openDonorId: number): OpenDonor => ({
  type: 'Donor/OPEN_DONOR',
  openDonorId,
});

export const closeDonor = (donorId: number): CloseDonor => ({
  type: 'Donor/CLOSE_DONOR',
  donorId,
});

export const updateDonorStatus = (donorId: number, closed: boolean, lastModified: string): UpdateDonorStatus => ({
  type: 'Donor/UPDATE_DONOR_STATUS',
  donorId,
  closed,
  lastModified,
});

export const pinCase = (caseId: number): PinCase => ({
  type: 'Donor/PIN_CASE',
  caseId,
});

export const pinnedCase = (caseId: number, pinNumber: number): PinnedCase => ({
  type: 'Donor/PINNED_CASE',
  caseId,
  pinNumber,
});

export const unpinCase = (caseId: number): UnpinCase => ({
  type: 'Donor/UNPIN_CASE',
  caseId,
});

export const unpinnedCase = (caseId: number): UnpinnedCase => ({
  type: 'Donor/UNPINNED_CASE',
  caseId,
});

export const updateTaskDueDate = (
  donorId: number,
  taskId: number,
  previousTaskId: number | null,
  interval: number | null,
  lastModified: string,
  dueDateComment: string | null,
): UpdateTaskDueDate => ({
  type: 'Donor/UPDATE_TASK_DUE_DATE',
  donorId,
  taskId,
  previousTaskId,
  interval,
  lastModified,
  dueDateComment,
});

export const updateTaskNote = (
  donorId: number,
  taskId: number,
  noteContent: string,
  lastModified: string,
): UpdateTaskNote => ({
  type: 'Donor/UPDATE_TASK_NOTE',
  donorId,
  taskId,
  noteContent,
  lastModified,
});

export const saveTaskNote = (noteContent: string): SaveTaskNote => ({
  type: 'Donor/SAVE_TASK_NOTE',
  noteContent,
});

export const setOPODonorId = (opoDonorId: string): SetOPODonorId => ({
  type: 'Donor/SET_OPO_ID',
  opoDonorId,
});

export const setUnosId = (unosId: string): SetUnosId => ({
  type: 'Donor/SET_UNOS_ID',
  unosId,
});

export const setWorkflowKey = (workflowKey: string): SetWorkflowKey => ({
  type: 'Donor/SET_WORKFLOW_KEY',
  workflowKey,
});

export const setHospital = (hospital: string): SetHospital => ({
  type: 'Donor/SET_HOSPITAL',
  hospital,
});

export const setSex = (sex?: string): SetSex => ({
  type: 'Donor/SET_SEX',
  sex,
});

export const setDateOfBirth = (dateOfBirth: string): SetDateOfBirth => ({
  type: 'Donor/SET_DOB',
  dateOfBirth,
});

export const setSelectedOrgans = (organs: Array<string>): SetSelectedOrgans => ({
  type: 'Donor/SET_SELECTED_ORGANS',
  organs,
});

export const setNewRace = (newRace: string): SetNewRace => ({
  type: 'Donor/SET_RACE',
  newRace,
});

export const setHighRisk = (highRisk: boolean): SetHighRisk => ({
  type: 'Donor/SET_HIGH_RISK',
  highRisk,
});

export const setTags = (tags: Array<Tag>): SetTags => ({
  type: 'Donor/SET_TAGS',
  tags,
});

export const setSort = (column: DonorSortBy, direction: SortDirection): SetSort => ({
  type: 'Donor/SET_SORT',
  column,
  direction,
});

export const getOrganizationMembers = (): GetOrganizationMembers => ({
  type: 'Donor/GET_ORG_MEMBERS',
});

export const receiveOrganizationMembers = (orgMembers: Array<UserProfile>): ReceiveOrganizationMembers => ({
  type: 'Donor/RECEIVE_ORG_MEMBERS',
  orgMembers,
});

export const receiveAvailableTags = (availableTags: Array<Tag>): ReceiveAvailableTags => ({
  type: 'Donor/RECEIVE_AVAILABLE_TAGS',
  availableTags,
});

export const receivePdfFields = (pdfFields: Array<DonorImportField>): ReceivePdfFields => ({
  type: 'Donor/RECEIVE_PDF_FIELDS',
  pdfFields,
});

export const loadPdfFields = (): LoadPdfFields => ({
  type: 'Donor/LOAD_PDF_FIELDS',
});

export const importDonorData = (
  caseId: number,
  importedDonorData: DonorDataEntry[],
  donorSource: DonorSource,
  existingDonorId?: number,
): ImportDonorData => ({
  type: 'Donor/IMPORT_DATA',
  caseId,
  importedDonorData,
  donorSource,
  existingDonorId,
});

export const importDonorDataSuccess = (caseId: number): ImportDonorDataSuccess => ({
  type: 'Donor/IMPORT_DATA_SUCCESS',
  caseId,
});

export const importDonorDataFailed = (): ImportDonorDataFailed => ({
  type: 'Donor/IMPORT_DATA_FAILED',
});

export const createDonor = (
  opoDonorId: string,
  highRisk: boolean,
  tags: Array<Tag>,
  workflowKey: string,
  hospital: string,
  sex: string,
  dateOfBirth: string,
  unosId: string,
  organs: Array<string>,
  importedDonorData?: Array<DonorDataEntry>,
  donorNetPdf?: File,
  existingDonorId?: number,
  selectedProviderServices?: Array<string>,
  stage?: CaseStage | null,
  isUnosImport?: boolean,
): CreateDonor => ({
  type: 'Donor/CREATE',
  opoDonorId,
  highRisk,
  tags,
  workflowKey,
  hospital,
  sex,
  dateOfBirth,
  unosId,
  organs,
  importedDonorData,
  donorNetPdf,
  existingDonorId,
  selectedProviderServices,
  stage,
  isUnosImport,
});

export const createDonorFailed = (errors?: DonorErrors): CreateDonorFailed => ({
  type: 'Donor/CREATE_FAILED',
  errors,
});

export const createDonorSuccess = (openDonorId: number): CreateDonorSuccess => ({
  type: 'Donor/CREATE_SUCCESS',
  openDonorId,
});

export const resetCreateDonorSuccess = (): ResetCreateDonorSuccess => ({
  type: 'Donor/RESET_CREATE_SUCCESS',
});

/**
 * @deprecated Use {@link patchUpdateCase} instead.
 */
export const updateDonor = (
  donorId: number,
  lastModified: string,
  opoDonorId: string,
  highRisk: boolean,
  tags: Array<Tag>,
  unosId: string | null,
  currentLocation: string,
  dob: string,
  typeOfDeath: string | null,
  causeOfDeath: string | null,
  race: string | null,
  sex: string | null,
  weight: string,
  height: string,
  organs: Array<string>,
  stage: CaseStage | null,
  rank: number | null,
  sequenceNumber: number | null,
): UpdateDonor => ({
  type: 'Donor/UPDATE',
  donorId,
  lastModified,
  opoDonorId,
  highRisk,
  tags,
  unosId,
  currentLocation,
  dob,
  typeOfDeath,
  causeOfDeath,
  race,
  sex,
  weight,
  height,
  organs,
  stage,
  rank,
  sequenceNumber,
});

export const updateDonorSuccess = (donorId: number, donor: Donor): UpdateDonorSuccess => ({
  type: 'Donor/UPDATE_SUCCESS',
  donorId,
  donor,
});

/**
 * Action to update individual fields of a case.
 * Nullable fields can be cleared out by passing null.
 */
export const patchUpdateCase = (
  caseId: number,
  lastModified: string,
  fields: {
    opoDonorId?: string | null;
    stage?: CaseStage | null;
    rank?: number | null;
    sequenceNumber?: number | null;
    highRisk?: boolean;
    tags?: Tag[] | null;
    xmatCaseId?: string | null;
    perfusionDeviceSn?: string | null;
  },
) => ({
  type: 'Donor/PATCH_UPDATE_CASE',
  caseId,
  lastModified,
  fields,
});

export const donorChanged = (donorUpdated: boolean): DonorChanged => ({
  type: 'Donor/CHANGED',
  donorUpdated,
});

export const filterDonors = (filter: string): FilterDonors => ({
  type: 'Donor/FILTER_DONORS',
  filter,
});

export const loadDonorFiles = (donorId: number): LoadDonorFiles => ({
  type: 'Donor/LOAD_FILES',
  donorId,
});

export const uploadDonorFile = (
  donorId: number,
  attachments: Array<AttachmentData>,
  isCaseAttachment?: boolean,
): UploadDonorFile => ({
  type: 'Donor/UPLOAD_DONOR_FILE',
  donorId,
  attachments: attachments || [],
  isCaseAttachment,
});

export const deleteDonorFile = (mediaFileId: number, donorId: number): DeleteDonorFile => ({
  type: 'Donor/DELETE_DONOR_FILE',
  mediaFileId,
  donorId,
});

export const deleteDonorFileSuccess = (): DeleteDonorFileSuccess => ({
  type: 'Donor/DELETE_DONOR_FILE_SUCCESS',
});

export const deleteDonorFileFailed = (message: string): DeleteDonorFileFailed => ({
  type: 'Donor/DELETE_DONOR_FILE_FAILED',
  message,
});

export const receiveDonorFiles = (donorId: number, fileMap: FileMap, fileIds: Array<MessageId>): ReceiveDonorFiles => ({
  type: 'Donor/RECEIVE_FILES',
  donorId,
  fileMap,
  fileIds,
});

export const setDonorSelectedFile = (id: number, selectedFile: AppFile): SetDonorSelectedFile => ({
  type: 'Donor/SET_SELECTED_FILE',
  id,
  selectedFile,
});

export const uploadDonorFileSuccess = (): UploadDonorFileSuccess => ({
  type: 'Donor/UPLOAD_DONOR_FILE_SUCCESS',
});

export const uploadDonorFileFailed = (message: string): UploadDonorFileFailed => ({
  type: 'Donor/UPLOAD_DONOR_FILE_FAILED',
  message,
});

export const receiveTasks = (
  workflow: string,
  tasks: Array<StaticTask>,
  sectionOrder: Array<string>,
): ReceiveTasks => ({
  type: 'Donor/RECEIVE_TASKS',
  workflow,
  tasks,
  sectionOrder,
});

export const getDonorTask = (donorId: number, taskId: number): GetDonorTask => ({
  type: 'Donor/GET_DONOR_TASK',
  donorId,
  taskId,
});

export const receiveDonorTask = (donorTask: DonorTask): ReceiveDonorTask => ({
  type: 'Donor/RECEIVE_DONOR_TASK',
  donorTask,
});

export const getDonorTaskData = (donorId: number): GetDonorTaskData => ({
  type: 'Donor/GET_DONOR_TASK_DATA',
  donorId,
});

export const receiveDonorTaskData = (donorId: number, taskData: TaskDataMap): ReceiveDonorTaskData => ({
  type: 'Donor/RECEIVE_DONOR_TASK_DATA',
  donorId,
  taskData,
});

export const updateTaskStatus = (
  donorId: number,
  taskId: number,
  lastModified: string,
  fieldValueMap: FormValueMap | undefined,
  completed: boolean | undefined,
  notApplicable: boolean | undefined,
  fromList: boolean,
  formDef: FormDefData,
): UpdateTaskStatus => ({
  type: 'Donor/UPDATE_TASK_STATUS',
  donorId,
  taskId,
  lastModified,
  fieldValueMap,
  completed,
  notApplicable,
  fromList,
  formDef,
});

export const openFollowerGroup = (followerGroupId?: number): OpenFollowerGroup => ({
  type: 'Donor/OPEN_FOLLOWER_GROUP',
  followerGroupId,
});

export const closeFollowerGroup = (followerGroupId: number): CloseFollowerGroup => ({
  type: 'Donor/CLOSE_FOLLOWER_GROUP',
  followerGroupId,
});

export const getDonorFollowerGroups = (donorId: number): GetDonorFollowerGroups => ({
  type: 'Donor/GET_DONOR_FOLLOWER_GROUPS',
  donorId,
});

export const getFollowerGroup = (
  donorId: number,
  followerGroupId: number,
  initEditState: boolean,
): GetFollowerGroup => ({
  type: 'Donor/GET_FOLLOWER_GROUP',
  donorId,
  followerGroupId,
  initEditState,
});

export const receiveDonorFollowerGroups = (
  donorId: number,
  followerGroupMap: FollowerGroupMap,
): ReceiveDonorFollowerGroups => ({
  type: 'Donor/RECEIVE_DONOR_FOLLOWER_GROUPS',
  donorId,
  followerGroupMap,
});

export const updateFollowerGroupTasks = (
  donorId: number,
  followerGroupId: number,
  taskIds: number[],
  muteNotification?: boolean,
): UpdateFollowerGroupTasks => ({
  type: 'Donor/UPDATE_FOLLOWER_GROUP_TASKS',
  donorId,
  followerGroupId,
  taskIds,
  muteNotification,
});

export const updateFollowerGroupPermissions = (
  donorId: number,
  followerGroupId: number,
  canUpdate: boolean,
): UpdateFollowerGroupPermissions => ({
  type: 'Donor/UPDATE_FOLLOWER_GROUP_PERMISSIONS',
  donorId,
  followerGroupId,
  canUpdate,
});

export const deleteFollowerGroup = (donorId: number, followerGroupId: number): DeleteFollowerGroup => ({
  type: 'Donor/DELETE_FOLLOWER_GROUP',
  donorId,
  followerGroupId,
});

export const updateTagFilter = (tags: string[]): DonorTagFilters => ({
  type: 'Donor/RECEIVE_TAG_FILTER',
  tags,
});

export const updateWorkflowTypeFilter = (workflowTypes: string[]): WorkflowTypeFilters => ({
  type: 'Donor/RECEIVE_WORKFLOW_FILTER',
  workflowTypes,
});

export const updateSessionCaseFilter = (sessionCaseStatusFilter: string): SessionCaseStatusFilter => ({
  type: 'Donor/RECEIVE_SESSION_CASE_FILTER',
  sessionCaseStatusFilter,
});

export const updateSessionCaseStageFilter = (sessionCaseStageFilter: CaseStage[]): SessionCaseStageFilter => ({
  type: 'Donor/RECEIVE_SESSION_CASE_STAGE_FILTER',
  sessionCaseStageFilter,
});

// Only the listed users are added or removed, so members added by someone else since the group was loaded are kept
export const updateFollowerGroupMembers = (
  donorId: number,
  followerGroupId: number,
  changes: FollowerGroupMemberChanges,
  muteNotification?: boolean,
): UpdateFollowerGroupMembers => ({
  type: 'Donor/UPDATE_FOLLOWER_GROUP_MEMBERS',
  donorId,
  followerGroupId,
  addUserIds: changes.addUserIds ?? [],
  removeUserIds: changes.removeUserIds ?? [],
  addExternalUserIds: changes.addExternalUserIds ?? [],
  removeExternalUserIds: changes.removeExternalUserIds ?? [],
  muteNotification,
});

export const updateDonorForm = (
  donorId: number,
  taskId: number,
  formValueMap: FormValueMap,
  lastModified: string,
  notify: boolean,
  notifyFullTask: boolean,
  notifyAllTasks: boolean,
): UpdateDonorForm => ({
  type: 'Form/UPDATE_DONOR_FORM',
  donorId,
  taskId,
  formValueMap,
  lastModified,
  notify,
  notifyFullTask,
  notifyAllTasks,
});

export const receiveCaseNotes = (caseId: number, notes: CaseNote[]): ReceiveCaseNotes => ({
  type: 'Donor/RECEIVE_CASE_NOTES',
  caseId,
  notes,
});

export const getCaseTimeline = (caseId: number): GetCaseTimeline => ({
  type: 'Donor/GET_CASE_TIMELINE',
  caseId,
});

export const receiveCaseTimeline = (caseId: number, timeline: CaseTimelineEntry[]): ReceiveCaseTimeline => ({
  type: 'Donor/RECEIVE_CASE_TIMELINE',
  caseId,
  timeline,
});

export const setCasePreferences = (casePreferences: FollowerEditState[]): SetCasePreferences => ({
  type: 'Donor/SET_CASE_PREFERENCES',
  casePreferences,
});

export const resetCasePreferences = (): ResetCasePreferences => ({ type: 'Donor/RESET_CASE_PREFERENCES' });
export const loadCasePreferences = (workflow: string, caseId: number): LoadCasePreferences => ({
  type: 'Donor/LOAD_CASE_PREFERENCES',
  workflow,
  caseId,
});

export const resetDonorEditState = (): ResetDonorEditState => ({
  type: 'Donor/RESET',
});

export const receiveTotalFilteredCases = (totalFilteredCases: number): ReceiveTotalFilteredCases => ({
  type: 'Donor/RECEIVE_TOTAL_FILTERED_CASES',
  totalFilteredCases,
});

export const setCaseDisplayOrder = (caseDisplayOrder: number[]): SetCaseDisplayOrder => ({
  type: 'Donor/SET_CASE_DISPLAY_ORDER',
  caseDisplayOrder,
});

export const incrementCaseUnreadCount = (caseId: number): IncrementCaseUnreadCount => ({
  type: 'Donor/INCREMENT_CASE_UNREAD_COUNT',
  caseId,
});

export const generateModularCasePDF = (caseId: number, sections: SelectedSections): GenerateModularCasePDF => ({
  type: 'Donor/GENERATE_MODULAR_CASE_PDF',
  caseId,
  sections,
});

export const setEditCaseTaskStatus = (caseId: number, taskId: number, editBy?: number): SetEditCaseTaskStatus => ({
  type: 'Donor/SET_EDIT_CASE_TASK_STATUS',
  caseId,
  taskId,
  editBy,
});

export const receiveEditCaseTaskStatus = (
  caseId: number,
  taskId: number,
  editBy?: number,
  editTime?: string,
): ReceiveEditCaseTaskStatus => ({
  type: 'Donor/RECEIVE_EDIT_CASE_TASK_STATUS',
  caseId,
  taskId,
  editBy,
  editTime,
});

export const enableDonorSelectionMode = (): EnableSelectionMode => ({
  type: 'Donor/ENABLE_SELECTION_MODE',
});
export const disableDonorSelectionMode = (): DisableSelectionMode => ({
  type: 'Donor/DISABLE_SELECTION_MODE',
});
export const resetDonorSelection = (donorIds?: number[]): ResetDonorSelection => ({
  type: 'Donor/RESET_DONOR_SELECTION',
  donorIds,
});

export const toggleDonorSelection = (donorId: number): ToggleDonorSelection => ({
  type: 'Donor/TOGGLE_DONOR_SELECTION',
  donorId,
});

export const toggleCaseTaskExpanded = (donorId: number, taskId: number): ToggleCaseTaskExpanded => ({
  type: 'Donor/TOGGLE_CASE_TASK_EXPANDED',
  donorId,
  taskId,
});

export const setCaseShowBlankFields = (donorId: number, showBlankFields: boolean): SetCaseShowBlankFields => ({
  type: 'Donor/SET_CASE_SHOW_BLANK_FIELDS',
  donorId,
  showBlankFields,
});

export const receiveOrganizationAlert = (
  caseId: number,
  alertId: number,
  alert: Partial<ApiCaseServiceAlert>,
): ReceiveOrganizationAlert => ({
  type: 'Donor/RECEIVE_ORGANIZATION_ALERT',
  caseId,
  alertId,
  alert,
});

export const setShouldRefreshCaseList = (shouldRefreshCaseList: boolean): SetShouldRefreshCaseList => ({
  type: 'Donor/SET_SHOULD_REFRESH_CASE_LIST',
  shouldRefreshCaseList,
});

export const loadProviderServices = (providerId: string): LoadProviderServices => ({
  type: 'Donor/LOAD_PROVIDER_SERVICES',
  providerId,
});

// CaseView action creators
export const createCaseView = (params: CreateCaseViewParams): CreateCaseView => ({
  type: 'Donor/CREATE_CASE_VIEW',
  params,
});

export const createCaseViewSuccess = (caseView: CaseView): CreateCaseViewSuccess => ({
  type: 'Donor/CREATE_CASE_VIEW_SUCCESS',
  caseView,
});

export const createCaseViewFailed = (error: string): CreateCaseViewFailed => ({
  type: 'Donor/CREATE_CASE_VIEW_FAILED',
  error,
});

export const updateCaseView = (params: UpdateCaseViewParams): UpdateCaseView => ({
  type: 'Donor/UPDATE_CASE_VIEW',
  params,
});

export const updateCaseViewSuccess = (caseView: CaseView): UpdateCaseViewSuccess => ({
  type: 'Donor/UPDATE_CASE_VIEW_SUCCESS',
  caseView,
});

export const updateCaseViewFailed = (error: string): UpdateCaseViewFailed => ({
  type: 'Donor/UPDATE_CASE_VIEW_FAILED',
  error,
});

export const deleteCaseView = (casesViewId: number): DeleteCaseView => ({
  type: 'Donor/DELETE_CASE_VIEW',
  casesViewId,
});

export const deleteCaseViewSuccess = (casesViewId: number): DeleteCaseViewSuccess => ({
  type: 'Donor/DELETE_CASE_VIEW_SUCCESS',
  casesViewId,
});

export const deleteCaseViewFailed = (error: string): DeleteCaseViewFailed => ({
  type: 'Donor/DELETE_CASE_VIEW_FAILED',
  error,
});

export const removeCaseAdminFromCase = (caseId: number, userId: number): RemoveCaseAdminFromCase => ({
  type: 'Donor/REMOVE_CASE_ADMIN_FROM_CASE',
  caseId,
  userId,
});

const reducer = (state: DonorState = initialState, action: Action): DonorState => {
  switch (action.type) {
    case 'Application/RESET_DATA':
    case 'Auth/LOGOUT':
      // Reset the store when logging out
      return {
        ...initialState,
      };

    case 'Donor/RECEIVE_DONORS': {
      const current = state.donors || {};
      const incoming = action.donors || {};

      // Collect only the rows that actually changed to minimize re-renders
      const updates = {} as DonorMap;
      let changed = false;

      Object.entries(incoming).forEach(([idStr, newCaseDonorMap]) => {
        const id = Number(idStr); // Convert key once
        const newCase = newCaseDonorMap;
        if (!newCase) return;
        const existing = current[id];

        const pinNumber = Object.hasOwn(newCase, 'pinNumber')
          ? newCase.pinNumber
          : existing
            ? existing.pinNumber
            : undefined;

        let rowChanged = !existing || existing.pinNumber !== pinNumber;
        const caseKeys = Object.keys(newCase) as Array<keyof Donor>;
        if (!rowChanged) {
          rowChanged = caseKeys.some((k) => existing[k] !== newCase[k]);
        }

        if (rowChanged) {
          updates[id] = {
            ...(existing || {}),
            ...newCase,
            pinNumber,
          };
          changed = true;
        }
      });

      // If no changes detected, return same state reference to prevent re-renders
      if (!changed) return state;

      // Return new state with only changed donors getting new object references
      return {
        ...state,
        donors: {
          ...current,
          ...updates,
        },
      };
    }

    case 'Donor/RECEIVE_SINGLE_DONOR':
      return {
        ...state,

        donors: {
          ...state.donors,

          [action.donorId]: {
            ...state.donors[action.donorId],

            ...action.donor,
            pinNumber: action.donor.pinNumber || undefined,
          },
        },
      };

    case 'Donor/PINNED_CASE':
      return {
        ...state,
        donors: {
          ...state.donors,
          [action.caseId]: {
            ...state.donors[action.caseId],
            pinNumber: action.pinNumber,
          },
        },
      };

    case 'Donor/UNPINNED_CASE':
      return {
        ...state,
        donors: {
          ...state.donors,
          [action.caseId]: {
            ...state.donors[action.caseId],
            pinNumber: undefined,
          },
        },
      };

    case 'Donor/RECEIVE_PDF_FIELDS':
      return {
        ...state,
        pdfImportFields: action.pdfFields,
      };

    case 'Donor/CREATE_SUCCESS':
      return {
        ...state,

        openDonorId: action.openDonorId,
        createDonorSuccess: true,
      };

    case 'Donor/RESET_CREATE_SUCCESS':
      return {
        ...state,

        createDonorSuccess: false,
      };

    case 'Donor/OPEN_DONOR':
      return {
        ...state,

        openDonorId: action.openDonorId,
      };

    case 'Donor/CLOSE_DONOR':
      return {
        ...state,

        openDonorId: action.donorId === state.openDonorId ? undefined : state.openDonorId,
      };

    case 'Donor/SET_OPO_ID':
      return {
        ...state,

        opoDonorId: action.opoDonorId,
      };

    case 'Donor/SET_UNOS_ID':
      return {
        ...state,

        unosId: action.unosId,
      };

    case 'Donor/SET_WORKFLOW_KEY':
      return {
        ...state,

        workflowKey: action.workflowKey,
      };

    case 'Donor/SET_HOSPITAL':
      return {
        ...state,

        hospital: action.hospital,
      };

    case 'Donor/SET_SEX':
      return {
        ...state,

        sex: action.sex,
      };

    case 'Donor/SET_HIGH_RISK':
      return {
        ...state,

        highRisk: action.highRisk,
      };

    case 'Donor/SET_TAGS':
      return {
        ...state,

        tags: action.tags,
      };

    case 'Donor/SET_SORT':
      return {
        ...state,

        sort: {
          column: action.column,
          direction: action.direction,
        },
      };

    case 'Donor/SET_DOB':
      return {
        ...state,

        dateOfBirth: action.dateOfBirth,
      };

    case 'Donor/SET_SELECTED_ORGANS':
      return {
        ...state,

        organs: [...action.organs],
      };

    case 'Donor/RECEIVE_ORG_MEMBERS':
      return {
        ...state,

        orgMembers: action.orgMembers,
      };

    case 'Donor/RECEIVE_AVAILABLE_TAGS':
      return {
        ...state,

        availableTags: action.availableTags,
      };

    case 'Donor/RECEIVE_WORKFLOW_FILTER':
      return {
        ...state,

        workflowTypes: action.workflowTypes,
      };

    case 'Donor/RECEIVE_SESSION_CASE_FILTER':
      return {
        ...state,

        sessionCaseStatusFilter: action.sessionCaseStatusFilter,
      };

    case 'Donor/RECEIVE_SESSION_CASE_STAGE_FILTER':
      return {
        ...state,

        sessionCaseStageFilter: action.sessionCaseStageFilter,
      };

    case 'Donor/RECEIVE_TAG_FILTER':
      return {
        ...state,

        caseTagFilter: action.tags,
      };

    case 'Donor/CREATE_FAILED':
      return {
        ...state,

        errors: action.errors,
      };

    case 'Donor/UPDATE_SUCCESS':
      return {
        ...state,

        donors: {
          ...state.donors,

          [action.donorId]: {
            ...state.donors[action.donorId],

            ...action.donor,
          },
        },

        donorUpdated: false,
      };

    case 'Donor/SET_SELECTED_FILE':
      return {
        ...state,

        donors: {
          ...state.donors,
          [action.id]: {
            ...state.donors[action.id],

            selectedFile: action.selectedFile,
          },
        },
      };

    case 'Donor/CHANGED':
      return {
        ...state,

        donorUpdated: action.donorUpdated,
      };

    case 'Donor/FILTER_DONORS':
      return {
        ...state,

        filter: action.filter,
      };
    case 'Donor/RECEIVE_FILES':
      return {
        ...state,
        donors: {
          ...state.donors,
          [action.donorId]: {
            ...state.donors[action.donorId],

            files: action.fileMap,
            fileIds: action.fileIds,
          },
        },
      };

    case 'Donor/RECEIVE_TASKS':
      return {
        ...state,

        workflowTasks: {
          ...state.workflowTasks,

          [action.workflow]: {
            ...state.workflowTasks[action.workflow],

            tasks: action.tasks,
            sectionOrder: action.sectionOrder,
          },
        },
      };

    case 'Donor/RECEIVE_DONOR_TASK':
      return {
        ...state,

        donorTask: action.donorTask,
        editNoteContent: action.donorTask.noteContent,
      };

    case 'Donor/SAVE_TASK_NOTE':
      return {
        ...state,

        editNoteContent: action.noteContent,
      };

    case 'Donor/RECEIVE_DONOR_TASK_DATA': {
      const currentCase = state.donors[action.donorId];
      if (!currentCase) {
        return {
          ...state,
        };
      }
      return {
        ...state,

        donors: {
          ...state.donors,

          [action.donorId]: {
            ...state.donors[action.donorId],

            taskData: {
              ...state.donors[action.donorId]?.taskData,

              ...action.taskData,
            },
          },
        },
      };
    }

    case 'Donor/OPEN_FOLLOWER_GROUP':
      return {
        ...state,
        openFollowerGroupId: action.followerGroupId,
      };

    case 'Donor/CLOSE_FOLLOWER_GROUP':
      return {
        ...state,
        openFollowerGroupId:
          action.followerGroupId === state.openFollowerGroupId ? undefined : state.openFollowerGroupId,
      };

    case 'Donor/RECEIVE_DONOR_FOLLOWER_GROUPS':
      return {
        ...state,
        donors: {
          ...state.donors,

          [action.donorId]: {
            ...state.donors[action.donorId],

            followerGroups: {
              ...action.followerGroupMap,
            },
          },
        },
      };

    case 'Donor/RECEIVE_CASE_NOTES':
      return {
        ...state,
        donors: {
          ...state.donors,

          [action.caseId]: {
            ...state.donors[action.caseId],

            notes: [...action.notes],
          },
        },
      };

    case 'Donor/RECEIVE_CASE_TIMELINE':
      return {
        ...state,
        donors: {
          ...state.donors,
          [action.caseId]: {
            ...state.donors[action.caseId],
            timeline: [...action.timeline],
          },
        },
      };

    case 'Donor/SET_CASE_PREFERENCES':
      return {
        ...state,
        casePreferences: action.casePreferences,
      };

    case 'Donor/RESET_CASE_PREFERENCES':
      return {
        ...state,
        casePreferences: [],
      };

    case 'Donor/RESET':
      return {
        ...state,

        opoDonorId: '',
        workflowKey: '',
        highRisk: false,
        tags: [],
        hospital: '',
        sex: undefined,
        dateOfBirth: undefined,
        unosId: '',
        organs: [],
        filter: '',
        errors: undefined,
      };

    case 'Donor/RECEIVE_TOTAL_FILTERED_CASES': {
      return {
        ...state,
        totalFilteredCases: action.totalFilteredCases,
      };
    }

    case 'Donor/SET_CASE_DISPLAY_ORDER': {
      return {
        ...state,
        caseDisplayOrder: action.caseDisplayOrder,
      };
    }

    case 'Donor/INCREMENT_CASE_UNREAD_COUNT': {
      const workflowCase = state.donors[action.caseId];
      if (workflowCase) {
        return {
          ...state,
          donors: {
            ...state.donors,

            [action.caseId]: {
              ...state.donors[action.caseId],

              unread: state.donors[action.caseId].unread + 1,
            },
          },
        };
      }
      return state;
    }

    case 'Donor/RECEIVE_EDIT_CASE_TASK_STATUS': {
      const tasks = state.donors[action.caseId]?.tasks;

      if (!tasks) {
        return state;
      }

      const taskIndex = tasks.findIndex((task) => task.taskId === action.taskId);

      if (taskIndex === -1) {
        return state;
      }

      const newTask = { ...tasks[taskIndex], editBy: action.editBy, editTime: action.editTime };

      return {
        ...state,
        donors: {
          ...state.donors,
          [action.caseId]: {
            ...state.donors[action.caseId],
            tasks: replaceWith(tasks, taskIndex, newTask),
          },
        },
      };
    }

    case 'Donor/SET_SHOULD_REFRESH_CASE_LIST': {
      return {
        ...state,
        shouldRefreshCaseList: action.shouldRefreshCaseList,
      };
    }

    case 'Donor/ENABLE_SELECTION_MODE':
      return {
        ...state,
        selectionModeEnabled: true,
      };

    case 'Donor/DISABLE_SELECTION_MODE':
      return {
        ...state,
        selectionModeEnabled: false,
      };

    case 'Donor/RESET_DONOR_SELECTION':
      return {
        ...state,
        selectedDonorIds: action.donorIds ? action.donorIds : [],
      };

    case 'Donor/TOGGLE_DONOR_SELECTION': {
      const selectedDonorIdsSet = new Set(state.selectedDonorIds);

      if (selectedDonorIdsSet.has(action.donorId)) {
        selectedDonorIdsSet.delete(action.donorId);
      } else {
        selectedDonorIdsSet.add(action.donorId);
      }

      return {
        ...state,
        selectedDonorIds: Array.from(selectedDonorIdsSet),
      };
    }

    case 'Donor/RECEIVE_ORGANIZATION_ALERT': {
      const caseToUpdate = state.donors[action.caseId];
      if (!caseToUpdate) {
        return state;
      }

      const alerts = caseToUpdate.alerts ?? [];
      const existingIdx = alerts.findIndex((alert) => alert.id === action.alertId);

      let newAlerts: CaseServiceAlert[];
      if (existingIdx === -1) {
        // This is a new alert for this case, so add it
        newAlerts = [...alerts, action.alert as ApiCaseServiceAlert];
      } else {
        const updatedAlert = { ...alerts[existingIdx], ...action.alert };
        newAlerts = alerts.toSpliced(existingIdx, 1, updatedAlert);
      }

      return {
        ...state,
        donors: {
          ...state.donors,
          [action.caseId]: {
            ...state.donors[action.caseId],
            hasNewServiceAlert: newAlerts.some((alert) => alert.status === OrganizationAlertStatus.New),
            alerts: newAlerts,
          },
        },
      };
    }

    case 'CaseAlerts/UPDATE_ALERT_STATUS_SUCCESS': {
      const caseToUpdate = state.donors[action.donorId];
      if (!caseToUpdate) {
        return state;
      }

      const alerts = caseToUpdate.alerts ?? [];
      const existingIdx = alerts.findIndex((alert) => alert.id === action.alert.id);

      if (existingIdx === -1) {
        return state;
      }

      const updatedAlert = { ...alerts[existingIdx], ...action.alert };
      const newAlerts = alerts.toSpliced(existingIdx, 1, updatedAlert);

      return {
        ...state,
        donors: {
          ...state.donors,
          [action.donorId]: {
            ...state.donors[action.donorId],
            hasNewServiceAlert: newAlerts.some((alert) => alert.status === OrganizationAlertStatus.New),
            alerts: newAlerts,
          },
        },
      };
    }

    case 'Donor/TOGGLE_CASE_TASK_EXPANDED': {
      const current = state.caseExpandedTasks[action.donorId] || [];
      const idx = current.indexOf(action.taskId);
      const updated = idx === -1 ? [...current, action.taskId] : current.filter((id) => id !== action.taskId);
      return {
        ...state,
        caseExpandedTasks: {
          ...state.caseExpandedTasks,
          [action.donorId]: updated,
        },
      };
    }

    case 'Donor/SET_CASE_SHOW_BLANK_FIELDS': {
      return {
        ...state,
        caseShowBlankFields: {
          ...state.caseShowBlankFields,
          [action.donorId]: action.showBlankFields,
        },
      };
    }

    case 'Donor/REMOVE_CASE_ADMIN_FROM_CASE': {
      const caseToUpdate = state.donors[action.caseId];
      if (!caseToUpdate) {
        return state;
      }
      const currentAdmins = state.donors[action.caseId].caseAdmins;
      const newAdmins = currentAdmins.filter((admin) => admin.id !== action.userId);

      return {
        ...state,
        donors: {
          ...state.donors,
          [action.caseId]: {
            ...state.donors[action.caseId],
            caseAdmins: newAdmins,
          },
        },
      };
    }

    default:
      return state;
  }
};

export default reducer as Reducer<DonorState>;
